import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import SignInWithTwitchButton from '../../components/SignInWithTwitchButton';
import { acceptLscInvite, getLscInvitePreview, LscApiError } from '../api';
import JoinDiscordPrompt from '../components/JoinDiscordPrompt';
import JoinInviteRecoveryPanel from '../components/JoinInviteRecoveryPanel';
import { LscLogo } from '../components/LscLogo';
import LoadingState from '../components/LoadingState';
import { hasConnectedDiscord } from '../discordIdentity';
import {
  resolveJoinInviteErrorRecovery,
  resolveJoinInvitePreviewRecovery,
  type JoinInviteRecovery,
} from '../joinInviteErrors';
import {
  JOIN_INVITE_ACCEPT_DETAIL,
  JOIN_INVITE_ALREADY_MEMBER_DETAIL,
  JOIN_INVITE_ALREADY_MEMBER_HEADLINE,
  JOIN_INVITE_GO_TO_LEAGUE,
  JOIN_INVITE_JOIN_BUTTON,
  JOIN_INVITE_JOINING,
  JOIN_INVITE_LINK_DETAIL,
  JOIN_INVITE_LINK_HEADLINE,
  JOIN_INVITE_PREVIEW_INTRO,
  JOIN_INVITE_SIGN_IN_DETAIL,
  JOIN_INVITE_SIGN_IN_TITLE,
  JOIN_INVITE_SUCCESS_DETAIL,
  JOIN_INVITE_SUCCESS_HEADLINE,
  shortenInviteCodeForDisplay,
} from '../joinMemberCopy';
import type { LscLeagueInvitePreviewResult } from '../types';
import { lscPath, lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const LscJoinCodePage: React.FC = () => {
  useLscContentColumn('narrow');
  const { code: rawCode } = useParams<{ code: string }>();
  const code = rawCode ? decodeURIComponent(rawCode) : '';
  const returnTo = lscReturnTo(code ? `/join/${encodeURIComponent(code)}` : '/join');
  const { user, loading, authError } = useUser();

  if (loading) {
    return <LoadingState message="Checking sign-in status..." />;
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto space-y-4" data-testid="lsc-join-code-auth-shell">
        <div className="card text-center">
          <LscLogo variant="hero" className="mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white mb-2">{JOIN_INVITE_SIGN_IN_TITLE}</h1>
          <p className="text-secondary-300 mb-2">{JOIN_INVITE_SIGN_IN_DETAIL}</p>
          {code ? (
            <p className="text-xs text-secondary-500 mb-6" data-testid="join-code-auth-invite-hint">
              Invite: {shortenInviteCodeForDisplay(code)}
            </p>
          ) : (
            <p className="text-sm text-secondary-400 mb-6">{JOIN_INVITE_LINK_DETAIL}</p>
          )}
          {authError && (
            <p className="mb-4 text-sm text-red-400" role="alert">
              {authError}
            </p>
          )}
          <SignInWithTwitchButton returnTo={returnTo} />
        </div>
        <Link to={lscPath('/join')} className="text-sm text-secondary-400 hover:text-white">
          Try another code
        </Link>
      </div>
    );
  }

  return <JoinCodeContent code={code} />;
};

function JoinCodeContent({ code }: { code: string }) {
  const { user } = useUser();
  const returnTo = lscReturnTo(code ? `/join/${encodeURIComponent(code)}` : '/join');
  const discordConnected = hasConnectedDiscord(user);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [recovery, setRecovery] = useState<JoinInviteRecovery | null>(null);
  const [joinedLeagueId, setJoinedLeagueId] = useState<string | null>(null);
  const [joinedMessage, setJoinedMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<LscLeagueInvitePreviewResult | null>(null);

  const loadPreview = useCallback(async () => {
    if (!code) {
      setLoading(false);
      setRecovery(
        resolveJoinInviteErrorRecovery(new LscApiError('No invite code provided.', 400, 'invite_invalid'), 'preview')
      );
      return;
    }

    setLoading(true);
    setRecovery(null);
    try {
      const data = await getLscInvitePreview(code);
      setPreview(data);
      const previewRecovery = resolveJoinInvitePreviewRecovery(data);
      if (previewRecovery) {
        setRecovery(previewRecovery);
      }
    } catch (err) {
      setRecovery(resolveJoinInviteErrorRecovery(err, 'preview'));
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    void loadPreview();
  }, [loadPreview]);

  const handleJoin = async () => {
    setAccepting(true);
    setRecovery(null);
    setJoinedMessage(null);
    try {
      const result = await acceptLscInvite(code);
      if (result.already_member) {
        const leagueName = result.league_name || preview?.league_name || 'this league';
        setJoinedMessage(result.message || JOIN_INVITE_ALREADY_MEMBER_DETAIL.replace('This invite', leagueName));
        setJoinedLeagueId(result.league_id || preview?.league_id || null);
        setPreview((prev) =>
          prev
            ? {
                ...prev,
                already_member: true,
                can_accept: false,
                message: result.message,
              }
            : prev
        );
        return;
      }
      if (result.joined || result.status === 'joined') {
        const leagueName = result.league_name || preview?.league_name || 'this league';
        setJoinedMessage(result.message || `You joined ${leagueName}.`);
        setJoinedLeagueId(result.league_id || preview?.league_id || null);
        return;
      }
      setRecovery(resolveJoinInviteErrorRecovery(new LscApiError(result.message || 'Could not join.', 400), 'accept'));
    } catch (err) {
      setRecovery(resolveJoinInviteErrorRecovery(err, 'accept'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) return <LoadingState />;

  const valid = preview?.valid === true && !recovery;
  const alreadyMember = preview?.already_member === true;
  const joined = Boolean(joinedMessage);
  const canJoin = preview?.can_accept === true && !joined && !alreadyMember && !recovery;
  const leagueId = joinedLeagueId || preview?.league_id || null;
  const leagueName = preview?.league_name || 'this league';

  return (
    <div className="space-y-6 max-w-lg" data-testid="lsc-join-code-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">{JOIN_INVITE_LINK_HEADLINE}</h1>
        <p className="text-sm text-secondary-400">{JOIN_INVITE_LINK_DETAIL}</p>
      </div>

      {recovery && <JoinInviteRecoveryPanel recovery={recovery} />}

      {valid && preview && (
        <div className="card space-y-4" data-testid="invite-preview-valid">
          <div>
            <p className="text-sm text-secondary-500 mb-1">{JOIN_INVITE_PREVIEW_INTRO}</p>
            <h2 className="text-xl font-semibold text-white">{preview.league_name}</h2>
            {preview.league_slug && (
              <p className="text-sm text-secondary-400">@{preview.league_slug}</p>
            )}
          </div>
          <dl className="grid gap-2 text-sm">
            {preview.expires_at && (
              <div className="flex justify-between gap-4">
                <dt className="text-secondary-500">Expires</dt>
                <dd className="text-secondary-200">{preview.expires_at}</dd>
              </div>
            )}
            {preview.remaining_uses != null && (
              <div className="flex justify-between gap-4">
                <dt className="text-secondary-500">Remaining uses</dt>
                <dd className="text-secondary-200">{preview.remaining_uses}</dd>
              </div>
            )}
          </dl>

          {(joined || alreadyMember) && (
            <div className="space-y-3" data-testid={joined ? 'invite-joined-success' : 'invite-already-member'}>
              <div>
                <p className="text-white font-medium">
                  {joined ? JOIN_INVITE_SUCCESS_HEADLINE : JOIN_INVITE_ALREADY_MEMBER_HEADLINE}
                </p>
                <p className="text-sm text-emerald-200 mt-1">
                  {joinedMessage ||
                    preview.message ||
                    (alreadyMember
                      ? JOIN_INVITE_ALREADY_MEMBER_DETAIL
                      : JOIN_INVITE_SUCCESS_DETAIL)}
                </p>
                {joined && !joinedMessage?.includes(leagueName) ? (
                  <p className="text-sm text-secondary-400 mt-1">{JOIN_INVITE_SUCCESS_DETAIL}</p>
                ) : null}
              </div>
              {leagueId && (
                <Link
                  to={lscPath(`/leagues/${leagueId}`)}
                  className="btn-primary inline-block"
                  data-testid="invite-go-to-league-link"
                >
                  {JOIN_INVITE_GO_TO_LEAGUE}
                </Link>
              )}
            </div>
          )}

          {canJoin && (
            <div className="space-y-3" data-testid="invite-can-accept">
              <p className="text-sm text-secondary-400">{JOIN_INVITE_ACCEPT_DETAIL}</p>
              <button
                type="button"
                className="btn-primary"
                disabled={accepting}
                onClick={() => void handleJoin()}
                data-testid="invite-join-league-button"
              >
                {accepting ? JOIN_INVITE_JOINING : JOIN_INVITE_JOIN_BUTTON}
              </button>
              <JoinDiscordPrompt
                connected={discordConnected}
                returnTo={returnTo}
                variant="prep"
              />
            </div>
          )}
        </div>
      )}

      <Link to={lscPath('/leagues')} className="text-sm text-secondary-400 hover:text-white">
        Back to leagues
      </Link>
    </div>
  );
}

export default LscJoinCodePage;
